import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError, type AxiosError } from 'axios';
import { produce, type Draft } from 'immer';
import _ from 'lodash';
import pino from 'pino';

import { isReassignedToSelf } from '../../components/pages/approvals/approvalHelpers';
import { requestConstants } from '../../constants/appConstants';
import { events } from '../../constants/events';
import { notificationStrings } from '../../constants/strings';
import { ApprovalRepository } from '../../repositories/approvalRepository';
import type { Approval, ApprovalCardRequest, ApprovalState } from '../../repositories/approvalRepository';
import type { ConversationEvent } from '../../repositories/conversationRepository';
import { queryClient } from '../../router/queryClient';
import { isCompanyAdmin, isCompanyOrSupportAdmin } from '../../selectors/userSelectors';
import { getCancelledWord } from '../../utils/translationUtil';
import { useZustand } from '../../zustandStore';
import type { User } from '../../zustandStore/slices';
import { requestKeys, userKeys } from '../query-keys';

import {
	getApprovalsCountGroups,
	getCurrentCombinedLists,
	groupApprovals,
	isUserAssignedInTheAproval,
	updateApprovalItems,
} from './approvalsUtils';

const logger = pino();

const approvalKeys = {
	getApprovals: (state: ApprovalState) => ['approvals', state] as const,
	closeStuckPendingApproval: (stuckPendingApprovalId: string) =>
		['approvals', stuckPendingApprovalId, 'close'] as const,
	combinedApprovalsData: isActivityPage => ['combinedApprovalsData', isActivityPage] as const,
	getTeamApprovers: (teamId: string, approverId: string) =>
		['approvals', teamId, 'teamId', approverId, 'approverId'] as const,
};

const approvalsQuery = (state: ApprovalState) =>
	queryOptions({
		queryKey: approvalKeys.getApprovals(state),
		queryFn: () => ApprovalRepository.getApprovals(state),
		staleTime: Number.POSITIVE_INFINITY,
	});

const fetchAndMergeApprovals = async () => {
	const approvalsResults = await Promise.allSettled([
		queryClient.fetchQuery(approvalsQuery('assigned')),
		queryClient.fetchQuery(approvalsQuery('completed')),
	]);

	const assignedApprovalsResponse =
		approvalsResults[0].status === 'fulfilled' ? approvalsResults[0].value : { approvals: [] };
	const completedApprovalsResponse =
		approvalsResults[1].status === 'fulfilled' ? approvalsResults[1].value : { approvals: [] };

	approvalsResults.forEach((result, index) => {
		if (result.status === 'rejected') {
			logger.error(`Error fetching ${index === 0 ? 'assigned' : 'completed'} approvals:`, result.reason);
		}
	});

	return {
		assignedApprovals: assignedApprovalsResponse.approvals,
		completedApprovals: completedApprovalsResponse.approvals,
	};
};

type CombinedApprovals = {
	assignedApprovals: Approval[];
	completedApprovals: Approval[];
	approvalsDashBoardCollection: Approval[];
	groupedApprovals: Record<'pending' | 'approved' | 'declined' | 'canceled', { approvals: Approval[]; count: number }>;
	approvalsCount: Record<'pending' | 'approved' | 'declined' | 'canceled', number>;
	isActivityPage: boolean;
};

export const getCombinedApprovalsData = (user: User, teamId: string, isActivityPage: boolean) => {
	const queryKey = approvalKeys.combinedApprovalsData(isActivityPage);
	const isUserCompanyOrSupportAdmin = isCompanyOrSupportAdmin(user?.roles ?? []);

	const queryFn = async (): Promise<CombinedApprovals> => {
		const cachedData = queryClient.getQueryData<CombinedApprovals>(queryKey);
		if (cachedData) {
			return cachedData;
		}
		const { assignedApprovals, completedApprovals } = await fetchAndMergeApprovals();

		const initialCombinedApprovals = [...assignedApprovals, ...completedApprovals];
		const currentCombinedLists = getCurrentCombinedLists({ initialCombinedApprovals });
		const updatedApprovalItems = updateApprovalItems({
			currentCombinedLists,
			teamId,
			isUserCompanyOrSupportAdmin,
			isActivityPage,
			user,
		});
		const allUpdatedApprovalItems = isActivityPage
			? {
					approvals: updatedApprovalItems.approvalsDashBoardCollection,
					count: updatedApprovalItems.approvalsDashBoardCollection.length,
				}
			: {
					approvals: updatedApprovalItems.approvalsPageCollection,
					count: updatedApprovalItems.approvalsPageCollection.length,
				};
		const groupedApprovals = groupApprovals(allUpdatedApprovalItems.approvals, isActivityPage);
		const approvalStates = ['pending', 'approved', 'declined', 'canceled'] as const;
		const approvalsCount = getApprovalsCountGroups(groupedApprovals, approvalStates);

		return {
			assignedApprovals,
			completedApprovals,
			groupedApprovals,
			approvalsCount,
			approvalsDashBoardCollection: updatedApprovalItems.approvalsDashBoardCollection,
			isActivityPage,
		};
	};

	return {
		queryKey,
		queryFn,
		staleTime: Number.POSITIVE_INFINITY,
		enabled: Boolean(user && user?.userId && teamId),
		retry: (failureCount: number, error: AxiosError | unknown) => {
			logger.error(error);
			return failureCount < 3 && isAxiosError(error) && error.status !== 404;
		},
	};
};

const findIndexByRequestId = (approvals: Approval[], requestId: string) =>
	approvals.findIndex(approval => approval.request?.requestId === requestId);

export const updateApprovalsData = async (user: User, streamDataEvents: ConversationEvent[]) => {
	const queryKeyActivity = approvalKeys.combinedApprovalsData(true);
	const queryKeyApproval = approvalKeys.combinedApprovalsData(false);
	const previousApprovalsData = queryClient.getQueryData<CombinedApprovals>(queryKeyApproval);
	const previousActivityData = queryClient.getQueryData<CombinedApprovals>(queryKeyActivity);
	if (!previousApprovalsData && !previousActivityData) {
		return;
	}

	if (streamDataEvents.length === 0) {
		return;
	}

	const approvalsUiEvents = new Set([
		events.types.purchases.approvalTask,
		events.types.purchases.virtualCardAdded,
		events.types.purchases.requestCancelled,
		events.types.purchases.requestRejected,
	]);

	const approvalsFromStream = streamDataEvents
		.filter(({ type }) => approvalsUiEvents.has(type))
		.map(({ data, type, requestId }) => {
			const task = data.task || data;
			return { ...task, type, requestId } as Approval;
		});

	if (approvalsFromStream.length === 0) {
		return;
	}

	for (const streamApproval of approvalsFromStream) {
		const requestId = streamApproval.requestId || 'N/A';
		const { type } = streamApproval;

		switch (type) {
			case events.types.purchases.approvalTask: {
				await handleApprovalTask(queryKeyApproval, user, streamApproval, requestId);
				await handleApprovalTask(queryKeyActivity, user, streamApproval, requestId);
				break;
			}
			case events.types.purchases.virtualCardAdded: {
				await handleVirtualCardAdded(queryKeyApproval, user, streamApproval, requestId);
				await handleVirtualCardAdded(queryKeyActivity, user, streamApproval, requestId);
				break;
			}
			case events.types.purchases.requestCancelled: {
				await handleRequestCancelled(queryKeyApproval, streamApproval, requestId);
				await handleRequestCancelled(queryKeyActivity, streamApproval, requestId);
				break;
			}
			case events.types.purchases.requestRejected: {
				await handleRequestRejected(queryKeyApproval, streamApproval, requestId);
				await handleRequestRejected(queryKeyActivity, streamApproval, requestId);
				break;
			}
			default:
			// No default
		}
	}
};

async function handleApprovalTask(queryKey, user: User, streamApproval: Approval, requestId: string) {
	const previousData = queryClient.getQueryData<CombinedApprovals>(queryKey);
	if (!previousData) {
		logger.warn('no previous Data for approvals');
		return;
	}

	// set new dashboardApprovals new data
	const previousTeamData = queryClient.getQueryData<string[]>([userKeys.getUserSettings, 'selectedTeamID']);
	const isUserCompanyOrSupportAdmin = isCompanyOrSupportAdmin(user?.roles ?? []);
	const previousDashboardApprovals = previousData?.approvalsDashBoardCollection || [];
	const currentCombinedLists = [...previousDashboardApprovals, streamApproval];
	const newApprovalsDashBoardCollection = isUserCompanyOrSupportAdmin
		? currentCombinedLists
		: currentCombinedLists.filter(approval => approval.request.teamId === previousTeamData?.[0]);

	if (queryKey?.[1] || isUserAssignedInTheAproval(user?.userId, streamApproval) || isCompanyAdmin(user?.roles || [])) {
		const pending = previousData.groupedApprovals.pending;

		if (findIndexByRequestId(pending.approvals, requestId) === -1) {
			await updateQueryData(previousData, queryKey, draft => {
				draft.groupedApprovals.pending.approvals.push({
					...streamApproval,
					isPending: true,
					isApproved: false,
					isDeclined: false,
					isCancelled: false,
				});
				draft.groupedApprovals.pending.count += 1;
				draft.approvalsDashBoardCollection = newApprovalsDashBoardCollection;
			});
		} else if (streamApproval.reassignedToUser && !isReassignedToSelf(streamApproval)) {
			// the current user forwarded the approval to another user
			// first forward and new event will come to this branch
			const { pending } = previousData.groupedApprovals;
			const pendingIndex = findIndexByRequestId(pending.approvals, requestId);
			if (pendingIndex !== -1) {
				const existingApproval = pending.approvals[pendingIndex];
				if (!existingApproval) {
					return;
				}
				const updatedApproval = {
					...existingApproval,
					...streamApproval,
				};
				await updateQueryData(previousData, queryKey, draft => {
					draft.groupedApprovals.pending.approvals[pendingIndex] = {
						...updatedApproval,
						isPending: true,
						isApproved: false,
						isDeclined: false,
						isCancelled: false,
					};
					draft.approvalsDashBoardCollection = newApprovalsDashBoardCollection;
				});
			}
		} else if (streamApproval.reassignedByUser) {
			// other event after first forward
			// here we get the new approval id
			// here we get also the first reassign event

			const shouldCheckReassignedToSelf =
				queryKey === approvalKeys.combinedApprovalsData(false) ? isReassignedToSelf(streamApproval) : true;

			const { pending } = previousData.groupedApprovals;
			const pendingIndex = findIndexByRequestId(pending.approvals, requestId);
			if (shouldCheckReassignedToSelf && pendingIndex !== -1) {
				const existingApproval = pending.approvals[pendingIndex];
				if (!existingApproval) {
					return;
				}
				const updatedApproval = {
					...existingApproval,
					...streamApproval,
					outcome: existingApproval.outcome === 'REASSIGNED' ? streamApproval.outcome : 'REASSIGNED',
				};
				await updateQueryData(previousData, queryKey, draft => {
					draft.groupedApprovals.pending.approvals[pendingIndex] = {
						...updatedApproval,
						isPending: true,
						isApproved: false,
						isDeclined: false,
						isCancelled: false,
					};
					draft.groupedApprovals.pending.count = draft.groupedApprovals.pending.approvals.length;
					draft.approvalsDashBoardCollection = newApprovalsDashBoardCollection;
				});
			}
		}
	} else {
		await updateQueryData(previousData, queryKey, draft => {
			draft.approvalsDashBoardCollection = newApprovalsDashBoardCollection;
		});
	}
}

async function handleVirtualCardAdded(queryKey, user: User, streamApproval: Approval, requestId: string) {
	const previousData = queryClient.getQueryData<CombinedApprovals>(queryKey);
	if (!previousData) {
		logger.warn('no previous Data for approvals');
		return;
	}
	const { pending } = previousData.groupedApprovals;
	const pendingIndex = findIndexByRequestId(pending.approvals, requestId);
	/*
	 * sometimes we get straight the "purchases.cardAdded" event but not a previous event
	 * for the same approval with the event purchases.approvalTask
	 */
	const previousDashboardApprovals = previousData?.approvalsDashBoardCollection ?? [];
	const currentCombinedLists = previousDashboardApprovals.map(approval =>
		approval.id === streamApproval.id ? streamApproval : approval,
	);

	if (!currentCombinedLists.some(approval => approval.id === streamApproval.id)) {
		currentCombinedLists.push(streamApproval);
	}
	const isUserCompanyOrSupportAdmin = isCompanyOrSupportAdmin(user?.roles ?? []);
	const previousTeamData = queryClient.getQueryData<string[]>([userKeys.getUserSettings, 'selectedTeamID']);
	const newApprovalsDashBoardCollection = isUserCompanyOrSupportAdmin
		? currentCombinedLists
		: currentCombinedLists.filter(approval => approval.request.teamId === previousTeamData?.[0]);

	await (pendingIndex === -1
		? updateQueryData(previousData, queryKey, draft => {
				draft.approvalsDashBoardCollection = newApprovalsDashBoardCollection;
			})
		: updateQueryData(previousData, queryKey, draft => {
				draft.groupedApprovals.pending.approvals.splice(pendingIndex, 1);
				draft.groupedApprovals.pending.count -= 1;
				draft.groupedApprovals.approved.approvals.push({
					...streamApproval,
					isPending: false,
					isApproved: true,
					isDeclined: false,
					isCancelled: false,
				});
				draft.groupedApprovals.approved.count += 1;
				draft.approvalsDashBoardCollection = newApprovalsDashBoardCollection;
			}));
}

async function handleRequestCancelled(queryKey, streamApproval: Approval, requestId: string) {
	const previousData = queryClient.getQueryData<CombinedApprovals>(queryKey);
	if (!previousData) {
		logger.warn('no previous Data for approvals');
		return;
	}

	if (!_.isEmpty(streamApproval.request)) {
		const { approved } = previousData.groupedApprovals;
		const approvalIndex = findIndexByRequestId(approved.approvals, requestId);
		await updateQueryData(previousData, queryKey, draft => {
			const approvalToUpdate = draft.groupedApprovals.approved.approvals[approvalIndex];
			if (!approvalToUpdate) {
				return;
			}
			const formattedLastEdit = new Date(Date.now()).toISOString();
			approvalToUpdate.outcome = requestConstants.outcomes.canceled;
			approvalToUpdate.request.lastEdit = formattedLastEdit;
			draft.groupedApprovals.canceled.approvals.push({
				...approvalToUpdate,
				isPending: false,
				isApproved: false,
				isDeclined: false,
				isCancelled: true,
			});
			draft.groupedApprovals.canceled.count += 1;
			draft.groupedApprovals.approved.approvals.splice(approvalIndex, 1);
			draft.groupedApprovals.approved.count -= 1;
			draft.assignedApprovals = draft.assignedApprovals.filter(
				assignedApproval => assignedApproval.subjectId !== requestId,
			);
		});
	}
}

async function handleRequestRejected(queryKey, streamApproval: Approval, requestId: string) {
	const previousData = queryClient.getQueryData<CombinedApprovals>(queryKey);
	if (!previousData) {
		logger.warn('no previous Data for approvals');
		return;
	}
	const { pending } = previousData.groupedApprovals;
	const pendingIndex = findIndexByRequestId(pending.approvals, requestId);
	if (pendingIndex !== -1) {
		await updateQueryData(previousData, queryKey, draft => {
			const approvalToUpdate = draft.groupedApprovals.pending.approvals[pendingIndex];
			if (!approvalToUpdate) {
				return;
			}
			const formattedLastEdit = new Date(Date.now()).toISOString();
			approvalToUpdate.outcome = requestConstants.outcomes.rejected;
			approvalToUpdate.request.state = streamApproval.state;
			approvalToUpdate.request.lastEdit = formattedLastEdit;
			draft.groupedApprovals.declined.approvals.push({
				...approvalToUpdate,
				isPending: false,
				isApproved: false,
				isDeclined: true,
				isCancelled: false,
			});
			draft.groupedApprovals.declined.count += 1;
			draft.groupedApprovals.pending.approvals.splice(pendingIndex, 1);
			draft.groupedApprovals.pending.count -= 1;
			draft.assignedApprovals = draft.assignedApprovals.filter(
				assignedApproval => assignedApproval.subjectId !== requestId,
			);
		});
	}
}

async function updateQueryData(
	previousData: CombinedApprovals,
	queryKey,
	updateFn: (draft: Draft<CombinedApprovals>) => void,
) {
	await queryClient.cancelQueries({ queryKey });
	queryClient.setQueryData(queryKey, produce(previousData, updateFn));
	await queryClient.invalidateQueries({
		queryKey,
		refetchType: 'all',
	});
	await queryClient.invalidateQueries({
		queryKey: ['approval'],
		refetchType: 'all',
	});
}

export const useCloseStuckPendingApprovalMutation = () => {
	// check if required
	return useMutation({
		mutationFn: (stuckPendingApprovalId: string) =>
			ApprovalRepository.closeStuckPendingApproval(stuckPendingApprovalId),
		onError: err => {
			logger.error(err);
		},
	});
};

const updateOnDeclineData = async subjectId => {
	const queryKeyActivity = approvalKeys.combinedApprovalsData(true);
	const queryKeyApproval = approvalKeys.combinedApprovalsData(false);
	const queryKeys = [queryKeyActivity, queryKeyApproval];
	const previousDataCollection = await Promise.all(
		queryKeys.map(async queryKey => {
			await queryClient.cancelQueries({ queryKey });
			const previousData = queryClient.getQueryData(queryKey) as CombinedApprovals;
			if (previousData) {
				const pendingApprovals = previousData.groupedApprovals.pending.approvals;
				const approvalIndex = pendingApprovals.findIndex(approval => approval.subjectId === subjectId);

				if (approvalIndex !== -1) {
					await updateQueryData(previousData, queryKey, draft => {
						const approvalToUpdate = draft.groupedApprovals.pending.approvals[approvalIndex];
						if (!approvalToUpdate) {
							return;
						}
						const formattedLastEdit = new Date(Date.now()).toISOString();
						approvalToUpdate.outcome = requestConstants.outcomes.rejected;
						approvalToUpdate.request.state = requestConstants.outcomes.rejected;
						approvalToUpdate.request.lastEdit = formattedLastEdit;
						draft.groupedApprovals.declined.approvals.push({
							...approvalToUpdate,
							isPending: false,
							isApproved: false,
							isDeclined: true,
							isCancelled: false,
						});
						draft.groupedApprovals.declined.count += 1;
						draft.groupedApprovals.pending.approvals.splice(approvalIndex, 1);
						draft.groupedApprovals.pending.count -= 1;
						draft.assignedApprovals = draft.assignedApprovals.filter(
							assignedApproval => assignedApproval.subjectId !== subjectId,
						);
					});
				}
			}
			return previousData;
		}),
	);
	return previousDataCollection;
};

export const useDeclinePendingApprovalMutationQuery = () => {
	const { setNotification, setShowConversationButtons, setPageLoading } = useZustand();
	const queryKeyActivity = approvalKeys.combinedApprovalsData(true);
	const queryKeyApproval = approvalKeys.combinedApprovalsData(false);

	return useMutation({
		mutationFn: ApprovalRepository.declinePendingApproval,
		onMutate: async ({ subjectId }) => {
			setPageLoading(true);
			const previousCollection = await updateOnDeclineData(subjectId);
			return { previousData: previousCollection };
		},
		onSuccess: async () => {
			await queryClient.invalidateQueries({
				queryKey: requestKeys.getRequestList(),
			});
			setShowConversationButtons(false);
			setNotification([
				{
					type: notificationStrings.SUCCESS,
					message: 'Request was declined.',
				},
			]);
		},

		onError: (err, _variables, context: { previousData: CombinedApprovals[] } | undefined) => {
			if (context?.previousData[0]) {
				queryClient.setQueryData(queryKeyActivity, context.previousData[0]);
			}
			if (context?.previousData[1]) {
				queryClient.setQueryData(queryKeyApproval, context.previousData[1]);
			}
			setNotification([
				{
					type: notificationStrings.ERROR,
					message: 'There was a problem declining the request. Please try again.',
				},
			]);
			logger.error(err);
		},
		onSettled: () => {
			setPageLoading(false);
		},
	});
};

const getTeamApproversQuery = ({ teamId, approverId }) => ({
	queryKey: approvalKeys.getTeamApprovers(teamId, approverId),
	queryFn: () => ApprovalRepository.getTeamApprovers(teamId, approverId),
	enabled: !!teamId,
});

export const useGetTeamApproversQuery = teamId => {
	return useQuery(getTeamApproversQuery(teamId));
};

export const useForwardRequestMutation = () => {
	const { setShowConversationButtons } = useZustand();
	return useMutation({
		mutationFn: ApprovalRepository.forwardRequest,
		onSuccess: async (_data, _variables) => {
			setShowConversationButtons(true);
		},
		onError: err => {
			logger.error(err);
		},
	});
};

const updateOnCancelData = async subjectId => {
	const queryKeyActivity = approvalKeys.combinedApprovalsData(true);
	const queryKeyApproval = approvalKeys.combinedApprovalsData(false);
	const queryKeys = [queryKeyActivity, queryKeyApproval];
	const previousDataCollection = await Promise.all(
		queryKeys.map(async queryKey => {
			await queryClient.cancelQueries({ queryKey });
			const previousData = queryClient.getQueryData(queryKey) as CombinedApprovals;
			if (previousData) {
				const approvedApprovals = previousData.groupedApprovals.approved.approvals;

				const approvalIndex = approvedApprovals.findIndex(approval => approval.subjectId === subjectId);

				if (approvalIndex !== -1) {
					await updateQueryData(previousData, queryKey, draft => {
						const approvalToUpdate = draft.groupedApprovals.approved.approvals[approvalIndex];
						if (!approvalToUpdate) {
							return;
						}
						const formattedLastEdit = new Date(Date.now()).toISOString();
						approvalToUpdate.outcome = requestConstants.outcomes.canceled;
						approvalToUpdate.request.lastEdit = formattedLastEdit;
						draft.groupedApprovals.canceled.approvals.push({
							...approvalToUpdate,
							isPending: false,
							isApproved: false,
							isDeclined: false,
							isCancelled: true,
						});
						draft.groupedApprovals.canceled.count += 1;
						draft.groupedApprovals.approved.approvals.splice(approvalIndex, 1);
						draft.groupedApprovals.approved.count -= 1;
						draft.assignedApprovals = draft.assignedApprovals.filter(
							assignedApproval => assignedApproval.subjectId !== subjectId,
						);
					});
				}
			}
			return previousData;
		}),
	);
	return previousDataCollection;
};

export const useCancelApprovalMutationQuery = () => {
	const { setNotification, setPageLoading } = useZustand();
	const queryKeyActivity = approvalKeys.combinedApprovalsData(true);
	const queryKeyApproval = approvalKeys.combinedApprovalsData(false);

	return useMutation({
		mutationFn: ApprovalRepository.cancelApproval,
		onMutate: async ({ subjectId }) => {
			setPageLoading(true);
			const previousCollection = await updateOnCancelData(subjectId);
			return { previousData: previousCollection };
		},
		onSuccess: () => {
			setNotification([
				{
					type: notificationStrings.SUCCESS,
					message: `Request was ${getCancelledWord('canceled')}.`,
				},
			]);
		},
		onError: (err, _variables, context: { previousData: CombinedApprovals[] } | undefined) => {
			if (context?.previousData[0]) {
				queryClient.setQueryData(queryKeyActivity, context.previousData[0]);
			}
			if (context?.previousData[1]) {
				queryClient.setQueryData(queryKeyApproval, context.previousData[1]);
			}
			setNotification([
				{
					type: notificationStrings.ERROR,
					message: `There was a problem ${getCancelledWord('canceling')} this card. Please try again.`,
				},
			]);
			logger.error(err);
		},
		onSettled: () => {
			setPageLoading(false);
		},
	});
};

const updateOnSubmitData = async variables => {
	const queryKeyActivity = approvalKeys.combinedApprovalsData(true);
	const queryKeyApproval = approvalKeys.combinedApprovalsData(false);
	const queryKeys = [queryKeyActivity, queryKeyApproval];
	const previousDataCollection = await Promise.all(
		queryKeys.map(async queryKey => {
			await queryClient.cancelQueries({ queryKey });
			const previousData = queryClient.getQueryData(queryKey) as CombinedApprovals;
			if (previousData) {
				const pendingApprovals = previousData.groupedApprovals.pending.approvals;

				const pendingIndex = pendingApprovals.findIndex(approval => approval.request.requestId === variables.requestId);
				if (pendingIndex !== -1) {
					await updateQueryData(previousData, queryKey, draft => {
						const pendingApproval = draft.groupedApprovals.pending.approvals[pendingIndex];
						if (!pendingApproval) {
							return;
						}
						const formattedLastEdit = new Date(Date.now()).toISOString();
						pendingApproval.outcome = requestConstants.outcomes.approved;
						pendingApproval.context.isProcessing = 'true';
						pendingApproval.request = {
							...pendingApproval.request,
							lastEdit: formattedLastEdit,
							virtualCardId: '',
							state: requestConstants.outcomes.approved,
						};
						draft.groupedApprovals.approved.approvals.push({
							...pendingApproval,
							isPending: false,
							isApproved: true,
							isDeclined: false,
							isCancelled: false,
						});
						draft.groupedApprovals.approved.count += 1;
						draft.groupedApprovals.pending.approvals.splice(pendingIndex, 1);
						draft.groupedApprovals.pending.count -= 1;
					});
				}
			}
			return previousData;
		}),
	);
	return previousDataCollection;
};

export const useSubmitApprovalMutationQuery = () => {
	const queryClient = useQueryClient();
	const { setNotification, setPageLoading } = useZustand();
	const queryKeyActivity = approvalKeys.combinedApprovalsData(true);
	const queryKeyApproval = approvalKeys.combinedApprovalsData(false);

	return useMutation({
		mutationFn: ApprovalRepository.approve,
		onMutate: async (variables: ApprovalCardRequest) => {
			const previousCollection = await updateOnSubmitData(variables);
			setPageLoading(false);
			return { previousData: previousCollection };
		},
		onSuccess: async () => {
			await queryClient.invalidateQueries({
				queryKey: requestKeys.getRequestList(),
			});

			setNotification([
				{
					type: notificationStrings.SUCCESS,
					message: 'The virtual card has been created.',
				},
			]);
		},
		onError: (
			error: AxiosError,
			_variables: ApprovalCardRequest,
			context:
				| {
						previousData: CombinedApprovals[];
				  }
				| undefined,
		) => {
			logger.error(error);
			if (context?.previousData[0]) {
				queryClient.setQueryData(queryKeyActivity, context.previousData[0]);
			}
			if (context?.previousData[1]) {
				queryClient.setQueryData(queryKeyApproval, context.previousData[1]);
			}

			if (error['response']?.status === 403) {
				setNotification([
					{
						type: notificationStrings.WARNING,
						message: String(error['response']?.data) || error.message,
					},
				]);
			} else if (error['response']?.status === 429) {
				setNotification([
					{
						type: notificationStrings.WARNING,
						message:
							'Your company has exceeded the limit for approvals made per minute. ' +
							'Please limit approvals to less than 10 per minute and try again later.',
					},
				]);
			} else {
				setNotification([
					{
						type: notificationStrings.WARNING,
						message: 'There was a problem creating the virtual card. Please try again.',
					},
				]);
			}
		},
	});
};
